import { useEffect, useRef, useMemo, useState } from 'react'
import { detailsModalAssignmentStore } from '../model/detailsModalAssignmentStore'
import { useIsMobile } from 'shared/hooks/useBreakpoint'
import { syncAssignmentSeen } from 'shared/utils/counselorSeenSync'

const parseDateSafe = value => {
	if (!value) return null

	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? null : value
	}

	if (typeof value === 'number') {
		const dt = new Date(value)
		return Number.isNaN(dt.getTime()) ? null : dt
	}

	const s = String(value).trim()
	if (!s) return null

	const isoDay = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
	if (isoDay) {
		const y = Number(isoDay[1])
		const m = Number(isoDay[2]) - 1
		const d = Number(isoDay[3])
		const local = new Date(y, m, d, 0, 0, 0, 0)
		return Number.isNaN(local.getTime()) ? null : local
	}

	const dt = new Date(s)
	return Number.isNaN(dt.getTime()) ? null : dt
}

const getDueDate = assignment =>
	parseDateSafe(
		assignment?.due_date ??
			assignment?.dueDate ??
			assignment?.due ??
			assignment?.deadline ??
			null,
	)

const calculateDaysRemaining = dueDate => {
	if (!dueDate) return null

	const today = new Date()
	today.setHours(0, 0, 0, 0)

	const due = new Date(dueDate)
	due.setHours(0, 0, 0, 0)

	const diffTime = due.getTime() - today.getTime()
	const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

	return diffDays
}

const formatDueDateText = days => {
	if (days === null || days === undefined) return 'No deadline'

	if (days < 0) {
		return `Due by ${Math.abs(days)} ${Math.abs(days) === 1 ? 'day' : 'days'}`
	}

	if (days === 0) return 'Due today'

	if (days === 1) return 'Due: tomorrow'

	return `Due: in ${days} days`
}

const formatSubmissionDate = dateString => {
	if (!dateString) return null

	try {
		return new Date(dateString).toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		})
	} catch {
		return null
	}
}

export default function useDetailsModalAssignment({
	isOpen,
	onClose,
	assignment,
	source = 'classroom',
	onAssignmentSynced,
}) {
	const modalRef = useRef(null)
	const [isEditModalOpen, setIsEditModalOpen] = useState(false)
	const isMobile = useIsMobile()

	useEffect(() => {
		if (!isOpen) return

		const handleEsc = e => {
			if (e.key === 'Escape') {
				onClose?.()
			}
		}

		document.addEventListener('keydown', handleEsc)
		return () => document.removeEventListener('keydown', handleEsc)
	}, [isOpen, onClose])

	const onBackdropMouseDown = e => {
		if (e.target === e.currentTarget) {
			onClose?.()
		}
	}

	const assignmentData = useMemo(
		() => assignment?.assignmentData || {},
		[assignment],
	)
	const assignmentStudents = assignment?.students
	const students = useMemo(
		() => (Array.isArray(assignmentStudents) ? assignmentStudents : []),
		[assignmentStudents],
	)

	const status = useMemo(() => {
		const statusValue = assignment?.status?.toLowerCase() || 'active'
		return statusValue
	}, [assignment])

	const isCompleted = useMemo(() => status === 'completed', [status])

	const dueDateFromAssignment = useMemo(
		() => assignmentData?.due_date || assignment?.due_date,
		[assignmentData, assignment],
	)

	const dueDate = useMemo(
		() => getDueDate({ due_date: dueDateFromAssignment }),
		[dueDateFromAssignment],
	)

	const daysRemaining = useMemo(
		() => calculateDaysRemaining(dueDate),
		[dueDate],
	)

	const dueDateText = useMemo(
		() => formatDueDateText(daysRemaining),
		[daysRemaining],
	)

	const assignmentTitle = useMemo(
		() => assignmentData?.title || assignment?.name || 'Assignment',
		[assignmentData, assignment],
	)

	const formattedStudents = useMemo(() => {
		const mapped = students.map((student, index) => ({
			...student,
			formattedSubmissionDate: formatSubmissionDate(student.submission_date),
			_originalIndex: index,
		}))
		mapped.sort((a, b) => {
			if (Boolean(a.isNew) !== Boolean(b.isNew)) {
				return a.isNew ? -1 : 1
			}
			return a._originalIndex - b._originalIndex
		})
		return mapped.map(({ _originalIndex, ...student }) => student)
	}, [students])

	const studentsSectionTitle = useMemo(
		() => (students.length > 1 ? 'Students' : 'Student Information'),
		[students.length],
	)

	const filteredStudents = formattedStudents

	const filterCounts = useMemo(() => {
		const all = formattedStudents.length
		const completed = formattedStudents.filter(
			s => s.status === 'completed',
		).length

		const notStarted = formattedStudents.filter(
			s => s.status === 'not_started',
		).length

		const isNew = formattedStudents.filter(s => s.isNew).length

		return { all, completed, notStarted, isNew }
	}, [formattedStudents])

	const activeFilter = detailsModalAssignmentStore.activeFilter

	const displayStudents = useMemo(() => {
		if (activeFilter === 'all') {
			return formattedStudents
		}

		return formattedStudents.filter(student => {
			if (activeFilter === 'new') {
				return Boolean(student.isNew)
			}

			if (activeFilter === 'completed') {
				return student.status === 'completed'
			}

			if (activeFilter === 'not_started') {
				return student.status === 'not_started'
			}

			return false
		})
	}, [formattedStudents, activeFilter])

	const areAllSelected =
		detailsModalAssignmentStore.areAllFilteredSelected(displayStudents)

	const selectedCount =
		detailsModalAssignmentStore.getSelectedCount(displayStudents)

	const [openAccordions, setOpenAccordions] = useState(new Set([0]))

	const handleFilterChange = filter => {
		detailsModalAssignmentStore.setActiveFilter(filter)
	}

	const handleSelectAll = () => {
		detailsModalAssignmentStore.selectAllStudents(displayStudents)
	}

	const handleClearSelection = () => {
		detailsModalAssignmentStore.clearSelection()
	}

	const handleAccordionToggle = index => {
		setOpenAccordions(prev => {
			const next = new Set(prev)
			if (next.has(index)) {
				next.delete(index)
			} else {
				next.add(index)
			}
			return next
		})
	}

	const filterButtons = useMemo(() => {
		const buttons = [
			{
				id: 'all',
				label: 'All',
				count: filterCounts.all,
			},
			{
				id: 'completed',
				label: 'Completed',
				count: filterCounts.completed,
			},
			{
				id: 'not_started',
				label: 'Not Started',
				count: filterCounts.notStarted,
			},
		]

		if (filterCounts.isNew > 0) {
			buttons.push({
				id: 'new',
				label: 'New',
				count: filterCounts.isNew,
			})
		}

		return buttons
	}, [filterCounts])

	useEffect(() => {
		if (isOpen) {
			detailsModalAssignmentStore.reset()
			if (source === 'dashboard' && filterCounts.isNew > 0) {
				detailsModalAssignmentStore.setActiveFilter('new')
			}
		}
	}, [isOpen, source, filterCounts.isNew])

	useEffect(() => {
		if (isOpen) {
			if (displayStudents.length > 0) {
				setOpenAccordions(new Set([0]))
			}
		}
	}, [isOpen, displayStudents.length])

	useEffect(() => {
		if (!isOpen || !assignment?.id) return
		if (!Array.isArray(assignmentStudents) || assignmentStudents.length === 0) {
			return
		}
		void syncAssignmentSeen(assignment.id, { onAfterSync: onAssignmentSynced })
	}, [isOpen, assignment?.id, assignmentStudents, onAssignmentSynced])

	return {
		modalRef,
		onBackdropMouseDown,
		assignmentData,
		students: formattedStudents,
		filteredStudents,
		filterCounts,
		displayStudents,
		activeFilter,
		areAllSelected,
		selectedCount,
		openAccordions,
		filterButtons,
		handleFilterChange,
		handleSelectAll,
		handleClearSelection,
		handleAccordionToggle,
		status,
		isCompleted,
		dueDate,
		dueDateText,
		assignmentTitle,
		studentsSectionTitle,
		isEditModalOpen,
		setIsEditModalOpen,
		isMobile,
	}
}
